import React, { useState, useEffect } from "react";
import Select from "react-select";
import "./ChangeAccessLevel.css";
import Preloader from "../../../Preloader/Preloader";
import {
  getUsersSummary,
  getAllAccessLevels,
  getUserById,
  changeUserLoginPasswordAccessLevel,
} from "../../../../api/api";

const customStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: "10px",
    border: "1px solid #E0E0E0",
    padding: "5px",
    fontFamily: "Manrope",
    fontSize: "14px",
    fontWeight: 300,
    lineHeight: "19.12px",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "10px",
    marginTop: "5px",
  }),
  option: (provided) => ({
    ...provided,
    fontFamily: "Manrope",
    fontSize: "14px",
    fontWeight: 300,
  }),
};

const ROLES_DESCRIPTION = [
  {
    id: 5,
    name: "АДМС",
    information: "администратор сайта",
    access: "Полный доступ: все страницы портала.",
  },
  {
    id: 6,
    name: "АДМП",
    information: "администратор проектов",
    access:
      "Создание и редактирование проектов, редактирование команд проектов. База данных контрагентов.",
  },
  {
    id: 13,
    name: "СИСТЕМАДМ",
    information: "регистрация пользователей",
    access:
      "Админка пользователей: только вкладки «Регистрация» и «Смена пароля». Нет доступа к редактированию пользователей, смене уровней доступа и списку всех пользователей.",
  },
  {
    id: 1,
    name: "РО",
    information: "руководитель отделения",
    access:
      "Финансы проекта (ограниченные вкладки), анализ трудозатрат. Табель отдела, отчёты по отделам. Согласования: переработки, отгулы, история, согласование проекта (не табели и не зарплата). Дисциплина: по отделу и по сотруднику.",
  },
  {
    id: 2,
    name: "НО",
    information: "начальник отдела",
    access:
      "Табель отдела, отчёты по отделам (доступ к своему отделу). Согласования: переработки, отгулы, история, согласование проекта. Дисциплина: по отделу и по сотруднику. ",
  },
  {
    id: 3,
    name: "РГ",
    information: "руководитель группы",
    access:
      "Табель отдела. Согласования: переработки, отгулы, история, согласование проекта. Дисциплина по сотруднику. ",
  },
  {
    id: 9,
    name: "ГИП",
    information: "главный инженер проекта",
    access:
      "Финансы проекта: полный доступ ко всем вкладкам (смета, план, факт и т.д.). Список проектов, переход в финансы проекта. Согласования: переработки, отгулы, история, согласование проекта. ",
  },
  {
    id: 7,
    name: "ФИН",
    information: "доступ к данным по финансам",
    access:
      "Админка пользователей: редактирование оклада пользователя и просмотр всех пользователей. Отчёт бухгалтера (отчет по сотрудникам, расчётные листы, зарплата). Финансы проекта (прогресс выплат, факт), анализ трудозатрат. База данных контрагентов. Табель отдела. Согласование проекта. Нет согласования переработок/отгулов/табелей/зарплаты.",
  },
  {
    id: 8,
    name: "ИК",
    information: "инспектор кадров",
    access:
      "Админка пользователей: редактирование и просмотр всех пользователей. Табель отдела, отпуска и больничные. Согласования: переработки, отгулы, история, согласование проекта (не согласование табелей). Дисциплина: по отделу и по сотруднику. Доступ к списку отчётов по всем пользователям. ",
  },
  {
    id: 12,
    name: "ТАБ",
    information: "согласование табеля",
    access: "Доступ к отправке согласования табеля своего отдела.",
  },
  {
    id: 11,
    name: "ПУБ",
    information: "создание постов на главной странице",
    access:
      "Создание и публикация постов/новостей на главной странице портала. Админка управления новостями (список, создание) — вместе с АДМС.",
  },
  {
    id: 4,
    name: "СО",
    information: "сотрудник отдела",
    access:
      "Базовая роль сотрудника. Согласование проекта (свои соглашения и сметы, если назначен согласующим). Остальной доступ определяется другими ролями и привязкой к отделам/проектам.",
  },
  {
    id: 10,
    name: "СУБ",
    information: "субподрядчики",
    access: "Роль для субподрядчиков.",
  },
];

function ChangeAccessLevel() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [allAccessLevels, setAllAccessLevels] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAccessLevels, setSelectedAccessLevels] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [usersRes, levelsRes] = await Promise.all([
          getUsersSummary({ pageSize: 1000 }),
          getAllAccessLevels(),
        ]);
        if (usersRes?.users) setUsers(usersRes.users);
        if (Array.isArray(levelsRes)) setAllAccessLevels(levelsRes);
      } catch (err) {
        console.error(err);
        setError("Не удалось загрузить список сотрудников или уровней доступа");
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, []);

  const userOptions = users.map((user) => ({
    value: user.id,
    label: `${user.surname || ""} ${user.name || ""}`.trim() || user.username,
    username: user.username,
  }));

  const accessLevelOptions = allAccessLevels.map((level) => ({
    value: level.id,
    label: level.name,
  }));

  const handleUserChange = async (option) => {
    setSelectedUser(option);
    setUserDetails(null);
    setSelectedAccessLevels([]);
    setError(null);

    if (!option) return;

    setLoadingUser(true);
    try {
      const user = await getUserById(option.value);
      if (!user) {
        setError("Не удалось загрузить данные пользователя");
        return;
      }
      setUserDetails(user);
      const currentLevels = (user.access_levels || []).map((level) => ({
        value: level.id,
        label: level.name,
      }));
      setSelectedAccessLevels(currentLevels);
    } catch (err) {
      console.error(err);
      setError("Ошибка при загрузке данных пользователя");
    } finally {
      setLoadingUser(false);
    }
  };

  const handleAccessLevelsChange = (selected) => {
    setSelectedAccessLevels(selected || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser || !userDetails) return;

    setSaving(true);
    setError(null);
    try {
      const accessLevelIds = selectedAccessLevels.map((opt) => opt.value);
      // Не передаём username — при обновлении только уровней доступа иначе бэкенд возвращает 409 (проверка уникальности по текущему логину)
      await changeUserLoginPasswordAccessLevel(selectedUser.value, {
        access_levels: accessLevelIds,
      });
      setShowSuccessPopup(true);
      setSelectedUser(null);
      setUserDetails(null);
      setSelectedAccessLevels([]);
      setTimeout(() => setShowSuccessPopup(false), 3000);
    } catch (err) {
      setError(err.message || "Ошибка при сохранении уровней доступа");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Preloader />;
  }

  return (
    <div className="change-access-level">
      <form className="change-access-level__form" onSubmit={handleSubmit}>
        <ul className="change-access-level__inputs">
          <li className="change-access-level__inputs-container change-access-level__inputs-container--full-width">
            <label className="change-access-level__label">
              Выберите сотрудника
            </label>
            <Select
              options={userOptions}
              onChange={handleUserChange}
              value={selectedUser}
              placeholder="Выберите сотрудника"
              styles={customStyles}
              isClearable
            />
          </li>
        </ul>

        {loadingUser && (
          <div className="change-access-level__loading">
            <Preloader />
          </div>
        )}

        {selectedUser && userDetails && !loadingUser && (
          <ul className="change-access-level__inputs">
            <li className="change-access-level__inputs-container change-access-level__inputs-container--full-width">
              <label className="change-access-level__label">
                Уровни доступа
              </label>
              <Select
                isMulti
                options={accessLevelOptions}
                value={selectedAccessLevels}
                onChange={handleAccessLevelsChange}
                placeholder="Добавьте или удалите уровни доступа"
                styles={customStyles}
                closeMenuOnSelect={false}
              />
            </li>
            <li className="change-access-level__inputs-container">
              <div className="change-access-level__buttons">
                <button
                  type="submit"
                  className="newreport__button button-send button-active"
                  disabled={saving}
                >
                  {saving ? "Сохранение…" : "Сохранить изменения"}
                </button>
              </div>
            </li>
          </ul>
        )}

        {error && <div className="change-access-level__error">{error}</div>}

        {showSuccessPopup && (
          <div className="change-access-level__popup-overlay">
            <div className="change-access-level__popup">
              <div className="change-access-level__popup-icon" />
              <p className="change-access-level__popup-text">
                Уровни доступа успешно обновлены
              </p>
            </div>
          </div>
        )}
      </form>

      <section className="change-access-level__roles-section">
        <h2 className="change-access-level__roles-title">
          Описание уровней доступа
        </h2>
        <p className="change-access-level__roles-intro">
          Ниже — расшифровка ролей и доступ к разделам портала по данным текущей
          реализации.
        </p>
        <table className="change-access-level__roles-table">
          <thead>
            <tr>
              <th className="change-access-level__roles-th">Роль</th>
              <th className="change-access-level__roles-th">
                Краткое описание
              </th>
              <th className="change-access-level__roles-th">
                Доступ на портале
              </th>
            </tr>
          </thead>
          <tbody>
            {ROLES_DESCRIPTION.map((role) => (
              <tr key={role.id}>
                <td className="change-access-level__roles-td change-access-level__roles-td--name">
                  {role.name}
                </td>
                <td className="change-access-level__roles-td change-access-level__roles-td--short">
                  {role.information}
                </td>
                <td className="change-access-level__roles-td change-access-level__roles-td--access">
                  {role.access}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

export default ChangeAccessLevel;
